import { useThemeStore } from '@/store/theme'
import { useRegisterStore } from '@/store/useRegisterStore'
import { AppBar, Box, Button, IconButton, Menu, MenuItem, Stack, SvgIcon, Toolbar, Typography } from '@mui/material'
import { IconMoon, IconSun } from '@tabler/icons-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'
import { Menu as MenuIcon, DarkMode, LightMode } from '@mui/icons-material'

const HeaderPublic = () => {
  const navigate = useNavigate()
  const { mode, toggleTheme } = useThemeStore()
  const [lastScrollY, setLastScrollY] = useState(0)
  const [isVisible, setIsVisible] = useState(true)
  const { toggleDrawer } = useRegisterStore()
  const theme = useTheme()

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const isMenuOpen = Boolean(menuAnchor)
  const handleCloseMenu = () => setMenuAnchor(null)

  useEffect(() => {
    const SCROLL_THRESHOLD = 10
    let ticking = false

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY

          if (Math.abs(currentScrollY - lastScrollY) > SCROLL_THRESHOLD) {
            setIsVisible(currentScrollY < lastScrollY || currentScrollY < 50)
            setLastScrollY(currentScrollY)
          }

          ticking = false
        })

        ticking = true
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [lastScrollY])

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: mode === 'light' ? theme.palette.overlay.light.card : theme.palette.overlay.dark.card,
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          transform: isVisible ? 'translateY(0)' : 'translateY(-100%)',
          transition: 'all 0.3s ease-in-out',
          '&::before': {
            content: '""',
            height: 4,
            background: theme.palette.gradient.primary,
            flexShrink: 0,
          },
        }}
      >
        <Toolbar
          sx={{
            justifyContent: 'space-between',
            px: { xs: 2, sm: 3, md: 10 },
            py: 2,
            minHeight: { xs: 64, md: 72 },
          }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{
              cursor: 'pointer',
              '&:hover': {
                '& img': {
                  transform: 'scale(1.02)',
                },
              },
            }}
            onClick={() => navigate('/')}
          >
            <Box
              component="img"
              src="/logo/ADB@2x.png"
              alt="Climate Portal"
              sx={{
                height: 39.715,
                width: 40,
                flexShrink: 0,
                transition: 'transform 0.2s ease-in-out',
              }}
            />
            <Stack direction="column" spacing={0} sx={{ '& > :not(style)': { marginLeft: 1 } }}>
              <Typography
                variant="h6"
                color="text.headerPrimary"
                sx={{
                  fontSize: { xs: '1.30rem', md: '1.25rem' },
                  fontWeight: 400,
                  lineHeight: 'normal',
                }}
              >
                Disaster Risk Analytics Explorer
              </Typography>
              <Typography
                variant="body2"
                color="text.headerSecondary"
                sx={{
                  fontSize: { xs: '0.65rem', md: '1rem' },
                  lineHeight: 1.5,
                }}
              >
                Enhancing Resilience for Asia & the Pacific
              </Typography>
            </Stack>
          </Stack>

          <Stack
            direction="row"
            spacing={{ xs: 1, md: 2 }}
            alignItems="center"
            display={{
              xs: 'none',
              sm: 'flex',
              md: 'flex',
            }}
          >
            <IconButton
              onClick={toggleTheme}
              sx={{
                color: 'text.primary',
                width: 40,
                height: 40,
                '&:focus': {
                  outline: 'none',
                },
                '&:focus-visible': {
                  outline: 'none',
                },
              }}
            >
              {mode === 'dark' ? <IconSun size={24} stroke={1.5} /> : <IconMoon size={24} stroke={1.5} />}
            </IconButton>

            <Button
              variant="cd-outlined"
              sx={{
                display: 'flex',
                alignItems: 'center',
                fontSize: { xs: '0.875rem', md: '1rem' },
                padding: '0px',
                fontStyle: 'normal',
                lineHeight: '24px',
                '&::before': {
                  borderRadius: '8px',
                },
                '&:hover': {
                  background: 'unset',
                },
              }}
              onClick={() => toggleDrawer()}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  margin: '12px 20px',
                  background: theme.palette.gradient.primary,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                <SvgIcon inheritViewBox sx={{ fontSize: 24, mr: 1 }}>
                  <image href={'/icons/ai-icon.svg'} />
                </SvgIcon>
                <span>Get Involved</span>
              </Box>
            </Button>
          </Stack>

          <IconButton
            onClick={(event) => setMenuAnchor(event.currentTarget)}
            sx={{ display: { xs: 'flex', sm: 'none' }, color: 'text.primary' }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={menuAnchor}
        open={isMenuOpen}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem
          onClick={() => {
            toggleTheme()
            handleCloseMenu()
          }}
        >
          {mode === 'dark' ? <LightMode sx={{ mr: 1 }} /> : <DarkMode sx={{ mr: 1 }} />}
          {mode === 'dark' ? 'Light' : 'Dark'}
        </MenuItem>
        <MenuItem
          onClick={() => {
            toggleDrawer()
            handleCloseMenu()
          }}
        >
          <SvgIcon inheritViewBox sx={{ fontSize: 24, mr: 1 }}>
            <image href={'/icons/ai-icon.svg'} />
          </SvgIcon>
          Get Involved
        </MenuItem>
      </Menu>
    </>
  )
}

export default HeaderPublic
